"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DiagramModel } from "@/lib/drawio/model";
import type { DiagramConfig } from "@/lib/drawio/config";
import DiagramSvg from "./DiagramSvg";
import type { DiagramSvgState } from "./DiagramSvg";
import DiagramOverlay from "./DiagramOverlay";
import type { OverlayAnchor } from "./DiagramOverlay";
import DiagramStepper from "./DiagramStepper";

/**
 * DiagramIsland — the interactive layer over a compiled diagram.
 *
 * Server-rendered like any client component (the SVG and all text are in
 * the HTML), then hydrated. Hover or keyboard focus highlights a cell and,
 * with `behaviours.reach`, everything that hangs on it along the edges.
 * Click, tap or Enter opens the overlay with the cell's explanation next to
 * the cell. Legend chips dim everything outside a group; the stepper walks
 * the reading path. Chips, stepper and caption sit above the drawing so the
 * figure ends with the picture. One event listener per figure (delegation on
 * `data-cell`), no DOM mutation outside React.
 */

interface DiagramIslandProps {
  model: DiagramModel;
  config: DiagramConfig;
  idPrefix: string;
  /** Caption line (hint, source, poster link) rendered directly above the drawing. */
  caption?: React.ReactNode;
}

function cellIdFrom(target: EventTarget | null): string | null {
  if (!(target instanceof Element)) return null;
  const group = target.closest("[data-cell]");
  return group ? group.getAttribute("data-cell") : null;
}

export default function DiagramIsland({ model, config, idPrefix, caption }: DiagramIslandProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [locked, setLocked] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  const [group, setGroup] = useState<string | null>(null);
  const [step, setStep] = useState(-1);

  const interactive = useMemo(() => {
    const names = new Map<string, string>();
    for (const [id, cellConfig] of Object.entries(config.cells)) {
      const cell = model.cells.find((c) => c.id === id);
      names.set(id, cellConfig.label ?? (cell ? cell.plainText : id));
    }
    return names;
  }, [config, model]);

  // Plain-text explanations become <desc> nodes inside the cells: read by
  // screen readers via aria-describedby and present in the DOM for crawlers.
  const descriptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const [id, cellConfig] of Object.entries(config.cells)) {
      map.set(id, cellConfig.detail.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim());
    }
    return map;
  }, [config]);

  const adjacency = useMemo(() => {
    const out = new Map<string, string[]>();
    const inn = new Map<string, string[]>();
    for (const edge of model.edges) {
      if (!edge.source || !edge.target) continue;
      out.set(edge.source, [...(out.get(edge.source) ?? []), edge.target]);
      inn.set(edge.target, [...(inn.get(edge.target) ?? []), edge.source]);
    }
    return { out, inn };
  }, [model]);

  const active = pinned ?? hovered;
  const steps = useMemo(() => config.steps ?? [], [config.steps]);
  const groups = useMemo(() => config.groups ?? {}, [config.groups]);

  const focus = useMemo<ReadonlySet<string> | null>(() => {
    if (step >= 0 && steps[step]) return new Set(steps[step].focus);
    if (group && groups[group]) return new Set(groups[group].cells);
    return null;
  }, [step, steps, group, groups]);

  const reach = useMemo<ReadonlySet<string> | null>(() => {
    const behaviour = config.behaviours?.reach;
    if (!behaviour || !active) return null;
    const seen = new Set<string>();
    const queue = [active];
    while (queue.length > 0) {
      const current = queue.shift() as string;
      const next = [
        ...(behaviour.direction !== "upstream" ? adjacency.out.get(current) ?? [] : []),
        ...(behaviour.direction !== "downstream" ? adjacency.inn.get(current) ?? [] : []),
      ];
      for (const id of next) {
        if (!seen.has(id)) {
          seen.add(id);
          queue.push(id);
        }
      }
    }
    return seen.size > 0 ? seen : null;
  }, [active, adjacency, config.behaviours]);

  const state: DiagramSvgState = { active, focus, reach };

  // Inside the guide's e-mail wall the drawing is in the HTML (crawlable) but
  // blurred; while the gate is locked the island is inert, so neither pointer
  // nor keyboard can open an overlay. The gate toggles its class on unlock.
  useEffect(() => {
    const gate = rootRef.current?.closest(".guide-gate");
    if (!gate) return undefined;
    const update = () => setLocked(gate.classList.contains("guide-gate--locked"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(gate, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const anchor = useMemo<OverlayAnchor | null>(() => {
    if (!pinned) return null;
    const cell = model.cells.find((c) => c.id === pinned);
    if (!cell) return null;
    const cx = ((cell.x + cell.w / 2) / model.width) * 100;
    const below = cell.y + cell.h < model.height * 0.62;
    return {
      x: Math.min(84, Math.max(16, cx)),
      y: ((below ? cell.y + cell.h : cell.y) / model.height) * 100,
      placement: below ? "below" : "above",
    };
  }, [pinned, model]);

  const focusCell = useCallback(
    (id: string | null) => {
      if (!id || typeof document === "undefined") return;
      const node = document.querySelector<HTMLElement>(`#${CSS.escape(idPrefix)} [data-cell="${CSS.escape(id)}"]`);
      node?.focus({ preventScroll: true });
    },
    [idPrefix],
  );

  const closeOverlay = useCallback(() => {
    setPinned((current) => {
      focusCell(current);
      return null;
    });
  }, [focusCell]);

  const reset = useCallback(() => {
    setHovered(null);
    setPinned(null);
    setGroup(null);
    setStep(-1);
  }, []);

  const onPointerOver = useCallback(
    (event: React.PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      const id = cellIdFrom(event.target);
      setHovered(id && interactive.has(id) ? id : null);
    },
    [interactive],
  );

  const onPointerLeave = useCallback(() => setHovered(null), []);

  const onClick = useCallback(
    (event: React.MouseEvent) => {
      const id = cellIdFrom(event.target);
      if (id && interactive.has(id)) {
        setPinned((current) => (current === id ? null : id));
      } else {
        // Background click closes the overlay.
        setPinned(null);
      }
    },
    [interactive],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        if (pinned) closeOverlay();
        else reset();
        return;
      }
      if (event.key === "Enter" || event.key === " ") {
        const id = cellIdFrom(event.target);
        if (id && interactive.has(id)) {
          event.preventDefault();
          setPinned((current) => (current === id ? null : id));
        }
      }
    },
    [pinned, closeOverlay, reset, interactive],
  );

  const onFocus = useCallback(
    (event: React.FocusEvent) => {
      const id = cellIdFrom(event.target);
      if (id && interactive.has(id)) setHovered(id);
    },
    [interactive],
  );

  const onBlur = useCallback(() => setHovered(null), []);

  const selectGroup = (id: string) => {
    setStep(-1);
    setPinned(null);
    setGroup((current) => (current === id ? null : id));
  };

  const goToStep = (index: number) => {
    setGroup(null);
    setPinned(null);
    setStep(index);
  };

  const groupEntries = Object.entries(groups);
  const anythingSet = pinned !== null || group !== null || step >= 0;
  const pinnedConfig = pinned ? config.cells[pinned] : undefined;

  const activeStep = step >= 0 ? steps[step] : null;

  return (
    <div
      ref={rootRef}
      className="dg"
      data-diagram={config.diagram}
      data-locked={locked ? "true" : undefined}
      {...(locked ? ({ inert: "" } as unknown as React.HTMLAttributes<HTMLDivElement>) : {})}
    >
      {steps.length > 0 || groupEntries.length > 0 ? (
        <div className="dg-toolbar">
          {steps.length > 0 ? <DiagramStepper steps={steps} current={step} labels={config.labels} onChange={goToStep} /> : null}
          {groupEntries.length > 0 ? (
            <div className="dg-legend" role="group" aria-label={config.labels.legend}>
              <span className="dg-toolbar-label">{config.labels.legend}</span>
              {groupEntries.map(([id, item]) => (
                <button key={id} type="button" className="dg-chip" aria-pressed={group === id} onClick={() => selectGroup(id)}>
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}
          {anythingSet ? (
            <button type="button" className="dg-chip dg-chip--reset" onClick={reset}>
              {config.labels.reset}
            </button>
          ) : null}
          {activeStep ? (
            <p className="dg-step-caption" aria-live="polite">
              <strong>{activeStep.title}.</strong> {activeStep.caption}
            </p>
          ) : null}
        </div>
      ) : null}

      {caption}

      <div className="dg-stage" onKeyDown={onKeyDown}>
        <div
          className="dg-scroll"
          onPointerOver={onPointerOver}
          onPointerLeave={onPointerLeave}
          onClick={onClick}
          onFocus={onFocus}
          onBlur={onBlur}
        >
          <DiagramSvg
            model={model}
            idPrefix={idPrefix}
            title={config.a11y.title}
            description={config.a11y.description}
            interactive={interactive}
            descriptions={descriptions}
            state={state}
          />
        </div>
        {pinned && pinnedConfig && anchor ? (
          <DiagramOverlay
            id={`${idPrefix}-overlay`}
            cell={pinnedConfig}
            title={interactive.get(pinned) ?? pinned}
            anchor={anchor}
            labels={config.labels}
            onClose={closeOverlay}
          />
        ) : null}
      </div>
    </div>
  );
}
